import { useTranslation } from 'react-i18next';
import { giftStatus, type Household } from '@guabi/shared';
import { useUpdateHousehold } from './queries';

/**
 * The gift this household sent, and whether they have been thanked.
 *
 * **Two dates and a note, not a checkbox.** "Thanked: yes" cannot answer the
 * question you actually have three months later, which is who has been waiting
 * longest — and the note is what lets you write a card that names the gift
 * rather than one that says "thank you for the lovely present", which is the
 * sentence that tells somebody you forgot.
 *
 * Both dates are set with one tap to today, because that is when you are
 * holding the gift or posting the card. The date input is there to correct it.
 */
export function GiftFields({ wedId, household }: { wedId: string; household: Household }) {
  const { t } = useTranslation();
  const save = useUpdateHousehold(wedId);
  const status = giftStatus(household);
  const today = new Date().toISOString().slice(0, 10);

  const set = (patch: { giftReceivedAt?: string; thankYouSentAt?: string; giftNote?: string }) =>
    save.mutate({
      householdId: household.householdId,
      input: { ...patch, updatedAt: household.updatedAt },
    });

  const dateRow = (
    label: string,
    value: string | undefined,
    key: 'giftReceivedAt' | 'thankYouSentAt',
  ) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="min-w-[8rem] text-caption text-leaf-soft">{label}</span>
      {value ? (
        <>
          <input
            type="date"
            value={value}
            onChange={(e) => set({ [key]: e.target.value })}
            className="min-h-tap rounded border border-line bg-paper px-2 text-body"
          />
          <button
            type="button"
            // An empty string clears it: the schema makes both optional, and a
            // gift logged against the wrong household has to be undoable.
            onClick={() => set({ [key]: '' })}
            className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
          >
            {t('common.remove')}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => set({ [key]: today })}
          className="min-h-tap text-caption text-brass-text underline underline-offset-4"
        >
          {t('gifts.markToday')}
        </button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-3 rounded border border-line bg-cream/40 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-caption font-medium text-ink">{t('gifts.title')}</span>
        <span className="text-caption text-leaf-soft">{t(`gifts.status.${status}`)}</span>
      </div>

      {dateRow(t('gifts.receivedOn'), household.giftReceivedAt, 'giftReceivedAt')}

      <label className="flex flex-col gap-1">
        <span className="text-caption text-leaf-soft">{t('gifts.note')}</span>
        <input
          defaultValue={household.giftNote ?? ''}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next !== (household.giftNote ?? '')) set({ giftNote: next });
          }}
          maxLength={300}
          placeholder={t('gifts.notePlaceholder')}
          className="min-h-tap rounded border border-line bg-paper px-2"
        />
      </label>

      {dateRow(t('gifts.thankedOn'), household.thankYouSentAt, 'thankYouSentAt')}

      {save.isError && (
        <span role="alert" className="text-caption text-status-declinedFg">
          {t('guests.renameFailed')}
        </span>
      )}
    </div>
  );
}
